import { DISPATCHER_SOURCE } from './dispatcher-handler.js';
import { REDACTION_RULES_VERSION } from './redaction.js';
import { RUNNER_WRAPPER } from './runner-wrapper.js';
import { DEPLOY_EXEC_ROLE_NAME, LOCK_TABLE_NAME, RUNNER_ROLE_NAME, RUN_LOG_GROUP, STATE_BUCKET_NAME_SUB, } from './template.js';
/**
 * The run dispatcher, deployed as a **second stack** in the customer's account.
 *
 * Separate from the bootstrap stack on purpose. The bootstrap stack is the trust
 * boundary and should change almost never; the grant window changes every
 * engagement. Mixing them would mean touching the IAM roles every time someone
 * extends access by an afternoon, and every such change is a chance to get the
 * trust boundary wrong.
 *
 * ## Where the grant is actually enforced
 *
 * On the **runner's own identity policy**: `sts:AssumeRole` on the deploy role,
 * conditioned on `aws:CurrentTime` being before the grant expiry. AWS refuses the
 * assume-role after that instant, so access closes whether or not the control
 * plane is up, honest, or remembered — no scheduled job has to fire, and there is
 * nothing to fail to fire.
 *
 * Two consequences worth being explicit about.
 *
 * **A run already in flight completes.** `aws:CurrentTime` is evaluated when the
 * role is assumed, not continuously, so a session issued inside the window lives
 * out its duration. That is deliberate: a Terraform apply killed halfway leaves
 * half-created infrastructure in a customer's account, which is far worse than a
 * grant overrunning by the length of one apply. The grant gates *starting* a run.
 *
 * **There is no scheduled revoke.** IM-26 called for one, and with a
 * `DateLessThan` condition in place it would add nothing: it is derived from the
 * same parameter, so it cannot catch a wrong value, and it could not close an
 * in-flight session either — which we have just decided we do not want. Its only
 * distinctive property is a failure mode the condition does not have. What is
 * scheduled instead is a *notification*, so a lapsed grant is visible rather than
 * merely effective.
 */
export const DISPATCHER_FUNCTION_NAME = 'QrveyRunDispatcher';
/**
 * v11 says which redaction rules the stack runs (IM-84).
 *
 * The redactor is inlined in this template, so the rules masking a customer's
 * logs are whatever was current when they last deployed it — and nothing could
 * read which that was. `RedactionRulesVersion` is the answer, and the account
 * screen reports a region whose rules are behind as its own step, because a
 * stale redactor writes secrets a newer rule would catch into the customer's log
 * group, which is a different order of problem from a stack a release behind.
 *
 * Additive, so the floor does not move: v5 to v10 carry rules v1 with no output
 * to say so, which `UNVERSIONED_REDACTION` records.
 *
 * v10 lets a run say where its Terraform state is (IM-139, IM-140).
 *
 * Both halves live in this stack: the dispatcher Lambda forwards `stateKey` and
 * `stateBucket`, and the runner wrapper in the task definition uses them
 * instead of building `{deployment_id}/state.tfstate`. Two instances could not
 * be operated without it — one adopted, whose state is wherever the installer
 * first put it, and one created without a deployment id, which the installer
 * does not require and this control plane did.
 *
 * **Additive, so the floor does not move.** A v9 account keeps working for
 * every run that names a deployment id, which is every run that exists today;
 * what it cannot do is run an adopted instance or one with no id. That is the
 * same visible-versus-blocking split v6 drew — the onboarding step reports it
 * as behind, and nothing refuses those accounts.
 */
export const DISPATCHER_VERSION = '11';
/**
 * The oldest dispatcher stack a run can actually reach.
 *
 * Distinct from `DISPATCHER_VERSION`, which is what this build *publishes*.
 * v3 is the floor because that is the version whose resource policy admits the
 * run worker's role (`qim-{env}-run-dispatch`) rather than the API's; below it a
 * cross-account invoke is refused. Everything v4 added is additive — the stack
 * building the registry secret itself — so a v3 stack still runs, it simply
 * cannot build that secret, and a customer supplying
 * `RegistryCredentialsSecretArn` does not need it to.
 *
 * The two constants exist separately because conflating them made an account
 * behind by one additive release report as unrunnable (IM-72): being *behind* is
 * worth showing, and being *unable to run* is a different fact.
 *
 * **v8 is the first release to raise it.** The external id is gone from the
 * deploy role's trust (bootstrap v5), from the runner's assume-role, and from
 * this stack's caller check — so the control plane no longer sends one, and a v7
 * dispatcher handed a payload without it refuses every run with
 * `external-id-mismatch`. There is nothing to degrade into; the account has to
 * redeploy the access, grant and dispatcher stacks. Doing that at all was only
 * reasonable because no customer had deployed yet (2026-09-17), and it would not
 * have been worth it a release later.
 *
 * v7 asked for *less* rather than more — `RunnerRoleArn` derived rather than
 * typed, `RunnerImage` prefilled — so it did not move the floor.
 *
 * v6 is additive in the same way and deliberately does **not** raise this floor.
 * It makes the runner wrapper assert the installer's own completion line before
 * exiting 0, so an apply that failed after Terraform errors stops being recorded
 * as a success (IM-80). An account still on v5 runs exactly as before — it
 * simply keeps reporting those runs as succeeded, which is the bug. Refusing
 * every such account would take the product away from customers to fix a
 * reporting fault; the onboarding screen showing them as behind is the right
 * pressure, and it already does.
 */
export const MINIMUM_DISPATCHER_VERSION = '8';
/**
 * The first dispatcher that hands the runner this account's registry credentials.
 *
 * Below it, every run has to carry a `registry_key` — the one variable that can
 * never be remembered, because `splitSecretVariables` strips it before the run
 * record is written. At or above it, the wrapper reads the secret the stack
 * already provisioned and the console stops asking (IM-118).
 *
 * Distinct from `MINIMUM_DISPATCHER_VERSION`, which stays at 8: an account
 * below this one runs perfectly well, it is simply less convenient, and
 * refusing those runs would take the product away to fix a convenience.
 */
export const REGISTRY_FROM_ACCOUNT_VERSION = '9';
const sub = (template) => ({ 'Fn::Sub': template });
/**
 * The runner's identity — derived, not asked for.
 *
 * It used to be a parameter, described as "the grant stack's RunnerRoleArn
 * output", and it was the very first field a customer saw. It never needed to
 * be: `RUNNER_ROLE_NAME` is fixed, the grant stack that creates the role
 * deploys into this same account, and the access template already builds this
 * exact ARN the same way. The comment defending the parameter argued that an
 * output someone can copy beats a name they have to know — true, and an
 * argument against the wrong alternative. `Fn::Sub` needs neither.
 *
 * The role still lives in the grant stack rather than here, for the reason it
 * moved there (IM-94): IAM is global, so a fixed role name in a per-region
 * stack fails with `already exists` on the second region. Deriving the ARN
 * does not change where the role is created, only who types it.
 */
const runnerRoleArn = sub(`arn:\${AWS::Partition}:iam::\${AWS::AccountId}:role/${RUNNER_ROLE_NAME}`);
/**
 * Placeholder only. The API fills in the real name, which is environment-shaped
 * (`qim-dev-run-dispatch`), so a template built without it names nothing real —
 * deliberately, rather than naming something plausible and wrong.
 */
export const DEFAULT_CONTROL_PLANE_ROLE = 'qim-run-dispatch';
export const buildDispatcherTemplate = (input) => ({
    AWSTemplateFormatVersion: '2010-09-09',
    Description: `Qrvey run dispatcher and access grant (dispatcher v${DISPATCHER_VERSION})`,
    Metadata: {
        'AWS::CloudFormation::Interface': {
            ParameterGroups: [
                {
                    Label: { default: 'Access' },
                    Parameters: ['GrantExpiresAt'],
                },
                {
                    Label: { default: 'Where the runner runs' },
                    Parameters: ['SubnetIds', 'SecurityGroupIds', 'AssignPublicIp'],
                },
                {
                    Label: { default: 'Runner image' },
                    Parameters: [
                        'RunnerImage',
                        'RegistryCredentialsSecretArn',
                        'RegistryUser',
                        'RegistryKey',
                    ],
                },
                {
                    Label: { default: 'Qrvey' },
                    Parameters: ['ControlPlaneAccountId', 'ControlPlaneRoleName', 'QrveyEventBusArn'],
                },
            ],
        },
    },
    Parameters: {
        /**
         * The grant. One format for three consumers: the IAM condition below,
         * EventBridge's `at()` expression, and the dispatcher's own comparison.
         * Offsets are deliberately not permitted — a single unambiguous UTC form
         * beats three parsers disagreeing about a timezone.
         */
        /**
         * An advisory copy of the grant expiry, for a faster and clearer refusal.
         *
         * **Optional, and empty is the recommended value.** The grant lives in the
         * grant stack and AWS enforces it at `AssumeRole`; this exists only so the
         * dispatcher can answer `grant-expired` immediately instead of launching a
         * task that pulls an image and then fails inside the container. Left empty,
         * the dispatcher skips the check and AWS decides — one source of truth, and
         * extending a grant means updating one stack rather than this one too.
         *
         * Set it only if the faster refusal is worth keeping this in step by hand.
         * A stale value is safe in one direction and not the other: earlier than
         * the real expiry refuses runs AWS would allow; later simply defers to AWS.
         */
        GrantExpiresAt: {
            Type: 'String',
            Default: '',
            Description: 'Leave empty. Optional copy of the grant expiry (yyyy-mm-ddThh:mm:ss UTC) for a faster refusal; the grant stack is authoritative.',
            AllowedPattern: '|\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}',
            ConstraintDescription: 'Empty, or UTC in the form 2026-08-27T17:30:00',
        },
        /*
         * No `ExternalId` here since v8, and that is the point of v8.
         *
         * This template held one only because the deploy role's trust demanded it,
         * and that demand was the diagnostics role's requirement copied onto a
         * same-account trust where it guarded nothing — see the note on
         * `DeployExecRole` in `template.ts`. Removing it there removed it from the
         * runner's container, from the dispatcher's caller check, and from the
         * payload the control plane sends on every run.
         *
         * So this artifact now carries no secret at all: it can be mailed to
         * whoever is doing the deploy, reviewed by their security team, and pasted
         * into a ticket, none of which was true before.
         */
        SubnetIds: {
            Type: 'List<AWS::EC2::Subnet::Id>',
            Description: 'Subnets the Terraform runner task runs in.',
        },
        SecurityGroupIds: {
            Type: 'List<AWS::EC2::SecurityGroup::Id>',
            Description: 'Security groups for the runner task. Egress only is sufficient.',
        },
        /**
         * The one network fact a customer has to get right, and the easy way to get
         * it wrong is to reason about the subnet instead of the task.
         *
         * A subnet's `MapPublicIpOnLaunch` governs EC2 instances and does **not**
         * apply to a Fargate task's ENI. So "I used public subnets" is not enough:
         * without this parameter the task has a private address only, its default
         * route goes to an internet gateway, and an IGW carries nothing for an ENI
         * with no public IP. The task then has no path out at all — which is worse
         * than a private subnet, where at least a NAT can exist.
         *
         * The failure is late and reads as something else. The task stops at
         * `ResourceInitializationError` before the container starts, so there is no
         * log stream and nothing to read: the first call it makes is to Secrets
         * Manager for the registry auth, and the message names *that* rather than
         * the network. Found on a real run in `us-west-2`, whose subnets were
         * public and whose task never started.
         *
         * The description is therefore the whole rule rather than the exception.
         * The default stays `DISABLED` — a public IP on a runner with
         * `AdministratorAccess` is not something to hand out by default — and being
         * explicit about when to change it is what the parameter is for.
         */
        AssignPublicIp: {
            Type: 'String',
            Default: 'DISABLED',
            AllowedValues: ['ENABLED', 'DISABLED'],
            Description: 'ENABLED if the subnets above are public: a Fargate task reaches the internet only through its own public IP, and a subnet that auto-assigns one to EC2 instances does not give one to this task. DISABLED if they are private and route to a NAT gateway. The runner needs egress to Secrets Manager, ECR and the image registry — with neither a public IP nor a NAT it stops before it starts, with no log to read.',
        },
        RunnerImage: {
            Type: 'String',
            Description: 'The qrvey-terraform installer image, supplied by Qrvey.',
            MinLength: 1,
            ...(input.runnerImage === undefined ? {} : { Default: input.runnerImage }),
        },
        /**
         * Where a finished run is reported.
         *
         * Optional, and empty by default, so a customer who declines to forward
         * events still gets a working dispatcher — runs simply stay unreconciled on
         * Qrvey's side. Making it mandatory would turn "we would rather not send you
         * events" into "you cannot deploy".
         */
        QrveyEventBusArn: {
            Type: 'String',
            Default: input.runEventBusArn ?? '',
            Description: "Optional. Qrvey's event bus, prefilled by Qrvey. Without it, Qrvey cannot see when a run finishes.",
        },
        RegistryCredentialsSecretArn: {
            Type: 'String',
            Default: '',
            Description: 'Optional. Secrets Manager ARN holding registry credentials you already manage. Leave blank and fill in the two fields below to have this stack create the secret for you.',
        },
        /**
         * The registry credentials themselves, so the stack can build the secret.
         *
         * ECS wants `{"username": ..., "password": ...}` and nothing else. Getting
         * that shape wrong produces a task that fails at image pull with an ECS
         * error naming neither the secret nor the shape, which is a bad first
         * experience of the product and the most error-prone step of onboarding. So
         * the template builds it, rather than a runbook asking someone to.
         */
        RegistryUser: {
            Type: 'String',
            Default: '',
            Description: 'Optional. The registry username Qrvey gave you. Leave blank if you supplied a secret ARN above.',
        },
        RegistryKey: {
            Type: 'String',
            Default: '',
            NoEcho: true,
            /**
             * Excludes `"` and `\` because the secret is assembled as JSON by
             * `Fn::Sub` and CloudFormation has no way to escape a value inside one.
             * A token containing either would produce a syntactically broken secret
             * that only fails at image pull; rejecting the stack says so immediately.
             * Azure Container Registry tokens are base64, so this rejects nothing real.
             */
            AllowedPattern: '^[A-Za-z0-9+/=_.~:-]{0,256}$',
            ConstraintDescription: 'Registry tokens are base64; quotes and backslashes are not permitted because the value is stored as JSON.',
            Description: 'Optional. The registry password or token Qrvey gave you. Leave blank if you supplied a secret ARN above.',
        },
        ControlPlaneAccountId: {
            Type: 'String',
            AllowedPattern: '\\d{12}',
            Default: input.controlPlaneAccountId,
        },
        ControlPlaneRoleName: {
            Type: 'String',
            Default: input.controlPlaneRoleName ?? DEFAULT_CONTROL_PLANE_ROLE,
            Description: 'The single Qrvey role permitted to invoke the dispatcher.',
        },
    },
    Conditions: {
        /**
         * Only when no ARN was given. A customer who already manages the secret
         * keeps managing it; supplying both would leave two secrets and no way to
         * tell which one ECS is reading.
         */
        CreatesRegistrySecret: {
            'Fn::And': [
                { 'Fn::Equals': [{ Ref: 'RegistryCredentialsSecretArn' }, ''] },
                { 'Fn::Not': [{ 'Fn::Equals': [{ Ref: 'RegistryKey' }, ''] }] },
            ],
        },
        HasRegistryCredentials: {
            'Fn::Or': [
                { 'Fn::Not': [{ 'Fn::Equals': [{ Ref: 'RegistryCredentialsSecretArn' }, ''] }] },
                { 'Fn::Not': [{ 'Fn::Equals': [{ Ref: 'RegistryKey' }, ''] }] },
            ],
        },
        ReportsCompletion: {
            'Fn::Not': [{ 'Fn::Equals': [{ Ref: 'QrveyEventBusArn' }, ''] }],
        },
    },
    Resources: {
        Cluster: {
            Type: 'AWS::ECS::Cluster',
            Properties: { ClusterName: 'qrvey-terraform-runner' },
        },
        /**
         * The registry credentials, in the one shape ECS reads.
         *
         * `repositoryCredentials` requires exactly `{"username":…,"password":…}`.
         * Building it here rather than asking for it in a runbook removes the step
         * most likely to be got wrong, and the failure it causes is opaque: an ECS
         * task that stops at image pull, naming neither the secret nor the shape.
         *
         * **Deliberately unnamed.** A `Name` would collide on re-deploy: Secrets
         * Manager keeps a deleted secret for a recovery window and refuses to create
         * another with the same name until it expires, so a customer who deleted and
         * re-ran this stack would be blocked for days.
         *
         * **Rotation does not need this stack.** ECS reads the secret at image pull,
         * so putting a new value in Secrets Manager takes effect on the next run.
         * The parameter is only the initial value — CloudFormation will report drift
         * afterwards, which is honest rather than a problem.
         */
        RegistrySecret: {
            Type: 'AWS::SecretsManager::Secret',
            Condition: 'CreatesRegistrySecret',
            Properties: {
                Description: 'Credentials for the registry holding the Qrvey installer image',
                SecretString: sub('{"username":"${RegistryUser}","password":"${RegistryKey}"}'),
            },
        },
        /**
         * Lets the runner read this account's registry credentials (IM-118).
         *
         * A `ManagedPolicy` with `Roles` rather than an inline policy, because the
         * role it attaches to belongs to the **grant** stack: the runner is created
         * once per account and the secret once per region, so the role's own stack
         * cannot name an ARN it will never know. Attaching from here keeps the two
         * lifecycles honest — deleting the dispatcher stack, which its own migration
         * begins with, takes the permission with the secret it was for.
         *
         * The execution role already holds this same read, and that is not the same
         * grant: that one is how *ECS* authenticates to pull the installer image,
         * and it is never in the container. This one is the runner reading a value
         * to hand to Terraform. Both are scoped to exactly the one secret.
         */
        RunnerRegistryReadPolicy: {
            Type: 'AWS::IAM::ManagedPolicy',
            Condition: 'HasRegistryCredentials',
            Properties: {
                Description: 'Lets the Qrvey Terraform runner read this account’s registry credentials',
                Roles: [RUNNER_ROLE_NAME],
                PolicyDocument: {
                    Version: '2012-10-17',
                    Statement: [
                        {
                            Effect: 'Allow',
                            Action: 'secretsmanager:GetSecretValue',
                            Resource: {
                                'Fn::If': [
                                    'CreatesRegistrySecret',
                                    { Ref: 'RegistrySecret' },
                                    { Ref: 'RegistryCredentialsSecretArn' },
                                ],
                            },
                        },
                    ],
                },
            },
        },
        /**
         * The runner's task role — and the grant boundary.
         *
         * This role is the only principal the deploy execution role trusts, and its
         * permission to assume that role is time-limited by AWS itself.
         */
        /** Pulls the image and writes task-level logs. Not the runner's own role. */
        RunnerExecutionRole: {
            Type: 'AWS::IAM::Role',
            Properties: {
                AssumeRolePolicyDocument: {
                    Version: '2012-10-17',
                    Statement: [
                        {
                            Effect: 'Allow',
                            Principal: { Service: 'ecs-tasks.amazonaws.com' },
                            Action: 'sts:AssumeRole',
                        },
                    ],
                },
                ManagedPolicyArns: [
                    sub('arn:${AWS::Partition}:iam::aws:policy/service-role/AmazonECSTaskExecutionRolePolicy'),
                ],
                Policies: [
                    {
                        'Fn::If': [
                            'HasRegistryCredentials',
                            {
                                PolicyName: 'ReadRegistryCredentials',
                                PolicyDocument: {
                                    Version: '2012-10-17',
                                    Statement: [
                                        {
                                            Effect: 'Allow',
                                            Action: 'secretsmanager:GetSecretValue',
                                            // Whichever of the two the customer chose. Scoped to that
                                            // one secret: the execution role has no business reading
                                            // anything else in their account.
                                            Resource: {
                                                'Fn::If': [
                                                    'CreatesRegistrySecret',
                                                    { Ref: 'RegistrySecret' },
                                                    { Ref: 'RegistryCredentialsSecretArn' },
                                                ],
                                            },
                                        },
                                    ],
                                },
                            },
                            { Ref: 'AWS::NoValue' },
                        ],
                    },
                ],
            },
        },
        TaskDefinition: {
            Type: 'AWS::ECS::TaskDefinition',
            Properties: {
                Family: 'qrvey-terraform-runner',
                RequiresCompatibilities: ['FARGATE'],
                NetworkMode: 'awsvpc',
                Cpu: '1024',
                Memory: '2048',
                // The image publishes arm64 tags too, but the multi-arch manifest is not
                // guaranteed for every build, and an architecture mismatch surfaces as a
                // task that starts and immediately dies. Pinned until a tag policy exists.
                RuntimePlatform: { CpuArchitecture: 'X86_64', OperatingSystemFamily: 'LINUX' },
                TaskRoleArn: runnerRoleArn,
                ExecutionRoleArn: { 'Fn::GetAtt': ['RunnerExecutionRole', 'Arn'] },
                ContainerDefinitions: [
                    {
                        // The dispatcher overrides this container by name; they must agree.
                        Name: 'runner',
                        Image: { Ref: 'RunnerImage' },
                        Essential: true,
                        /**
                         * The installer image assumes nothing and writes no config, so this
                         * wrapper does both before handing over. It lives here rather than
                         * in the dispatcher's container override because ECS can override a
                         * container's `command` but not its `entryPoint` — and because the
                         * customer should read it in the template they already review.
                         *
                         * The trailing 'qrvey-run' is `$0` for `bash -c`, so the verb the
                         * dispatcher supplies as `command` arrives as `$1` onward.
                         */
                        EntryPoint: ['/bin/bash', '-c', RUNNER_WRAPPER, 'qrvey-run'],
                        // Fixed for the account, so not something the caller can influence.
                        // The external id is no secret from this account: the customer typed
                        // it into the bootstrap stack themselves.
                        Environment: [
                            {
                                Name: 'QRVEY_DEPLOY_ROLE_ARN',
                                Value: sub(`arn:\${AWS::Partition}:iam::\${AWS::AccountId}:role/${DEPLOY_EXEC_ROLE_NAME}`),
                            },
                            {
                                Name: 'QRVEY_STATE_BUCKET',
                                Value: sub(STATE_BUCKET_NAME_SUB),
                            },
                            { Name: 'QRVEY_LOCK_TABLE', Value: LOCK_TABLE_NAME },
                            { Name: 'QRVEY_REGION', Value: { Ref: 'AWS::Region' } },
                            {
                                /*
                                 * Where this account keeps its registry credentials, so a run
                                 * does not have to carry them (IM-118).
                                 *
                                 * The ARN, not the value: the wrapper reads it with the runner's
                                 * own identity, and an environment variable is visible in
                                 * `DescribeTaskDefinition` to anyone who can call it. Empty when
                                 * the customer supplied no credentials, which the wrapper treats
                                 * as "the run must bring its own".
                                 */
                                Name: 'QRVEY_REGISTRY_SECRET_ARN',
                                Value: {
                                    'Fn::If': [
                                        'HasRegistryCredentials',
                                        {
                                            'Fn::If': [
                                                'CreatesRegistrySecret',
                                                { Ref: 'RegistrySecret' },
                                                { Ref: 'RegistryCredentialsSecretArn' },
                                            ],
                                        },
                                        '',
                                    ],
                                },
                            },
                        ],
                        RepositoryCredentials: {
                            'Fn::If': [
                                'HasRegistryCredentials',
                                {
                                    CredentialsParameter: {
                                        'Fn::If': [
                                            'CreatesRegistrySecret',
                                            { Ref: 'RegistrySecret' },
                                            { Ref: 'RegistryCredentialsSecretArn' },
                                        ],
                                    },
                                },
                                // A public registry needs none. Omitted rather than empty: ECS
                                // rejects a blank CredentialsParameter.
                                { Ref: 'AWS::NoValue' },
                            ],
                        },
                        LogConfiguration: {
                            LogDriver: 'awslogs',
                            Options: {
                                'awslogs-group': RUN_LOG_GROUP,
                                'awslogs-region': { Ref: 'AWS::Region' },
                                'awslogs-stream-prefix': 'run',
                            },
                        },
                    },
                ],
            },
        },
        DispatcherRole: {
            Type: 'AWS::IAM::Role',
            Properties: {
                AssumeRolePolicyDocument: {
                    Version: '2012-10-17',
                    Statement: [
                        {
                            Effect: 'Allow',
                            Principal: { Service: 'lambda.amazonaws.com' },
                            Action: 'sts:AssumeRole',
                        },
                    ],
                },
                ManagedPolicyArns: [
                    sub('arn:${AWS::Partition}:iam::aws:policy/service-role/AWSLambdaBasicExecutionRole'),
                ],
                Policies: [
                    {
                        PolicyName: 'StartTheRunner',
                        PolicyDocument: {
                            Version: '2012-10-17',
                            Statement: [
                                {
                                    Effect: 'Allow',
                                    Action: 'ecs:RunTask',
                                    Resource: sub('arn:${AWS::Partition}:ecs:${AWS::Region}:${AWS::AccountId}:task-definition/qrvey-terraform-runner:*'),
                                    // Scoped to this cluster, so the dispatcher cannot start the
                                    // runner somewhere unmonitored.
                                    Condition: {
                                        ArnEquals: { 'ecs:cluster': { 'Fn::GetAtt': ['Cluster', 'Arn'] } },
                                    },
                                },
                                {
                                    // Required to hand the task its roles, and narrowed to exactly
                                    // those two — an unscoped iam:PassRole is an escalation path to
                                    // any role in the account.
                                    Effect: 'Allow',
                                    Action: 'iam:PassRole',
                                    Resource: [runnerRoleArn, { 'Fn::GetAtt': ['RunnerExecutionRole', 'Arn'] }],
                                    Condition: { StringEquals: { 'iam:PassedToService': 'ecs-tasks.amazonaws.com' } },
                                },
                                {
                                    Effect: 'Allow',
                                    Action: 'ecs:TagResource',
                                    Resource: '*',
                                    Condition: { StringEquals: { 'ecs:CreateAction': 'RunTask' } },
                                },
                            ],
                        },
                    },
                ],
            },
        },
        Dispatcher: {
            Type: 'AWS::Lambda::Function',
            Properties: {
                FunctionName: DISPATCHER_FUNCTION_NAME,
                Description: 'The only thing Qrvey can invoke in this account.',
                Runtime: 'nodejs22.x',
                Handler: 'index.handler',
                Architectures: ['arm64'],
                Timeout: 30,
                MemorySize: 256,
                Role: { 'Fn::GetAtt': ['DispatcherRole', 'Arn'] },
                // Inlined rather than pulled from a Qrvey-controlled bucket: the customer
                // can read exactly what they are agreeing to run, and there is no
                // artifact that could be swapped after they reviewed it.
                Code: { ZipFile: DISPATCHER_SOURCE },
                Environment: {
                    Variables: {
                        GRANT_EXPIRES_AT: { Ref: 'GrantExpiresAt' },
                        CLUSTER_ARN: { 'Fn::GetAtt': ['Cluster', 'Arn'] },
                        TASK_DEFINITION_ARN: { Ref: 'TaskDefinition' },
                        SUBNET_IDS: { 'Fn::Join': [',', { Ref: 'SubnetIds' }] },
                        SECURITY_GROUP_IDS: { 'Fn::Join': [',', { Ref: 'SecurityGroupIds' }] },
                        ASSIGN_PUBLIC_IP: { Ref: 'AssignPublicIp' },
                    },
                },
            },
        },
        /**
         * The whole of the control plane's access to this account's write path.
         *
         * One function, one action, one principal. Scoped to a named role rather than
         * the account, so a compromise of some unrelated service in Qrvey's account
         * cannot start a deployment here.
         */
        DispatcherInvokePermission: {
            Type: 'AWS::Lambda::Permission',
            Properties: {
                FunctionName: { 'Fn::GetAtt': ['Dispatcher', 'Arn'] },
                Action: 'lambda:InvokeFunction',
                Principal: sub('arn:${AWS::Partition}:iam::${ControlPlaneAccountId}:role/${ControlPlaneRoleName}'),
            },
        },
        /**
         * Tells Qrvey a run finished.
         *
         * An event, not a poll. The control plane cannot watch a task it did not
         * start and has no standing credentials for, and polling would mean either a
         * long-lived session into this account or a schedule that keeps running long
         * after the grant has closed. This fires once, from the customer's own
         * account, and carries no data beyond what the task did.
         *
         * Scoped to the runner cluster and to STOPPED, so it forwards the end of a
         * Qrvey run and nothing else that happens in this account.
         */
        RunCompletionRule: {
            Type: 'AWS::Events::Rule',
            Condition: 'ReportsCompletion',
            Properties: {
                Name: 'qrvey-run-completion',
                Description: 'Forwards the end of a Qrvey run to Qrvey. Nothing else.',
                EventPattern: {
                    source: ['aws.ecs'],
                    'detail-type': ['ECS Task State Change'],
                    detail: {
                        clusterArn: [{ 'Fn::GetAtt': ['Cluster', 'Arn'] }],
                        lastStatus: ['STOPPED'],
                    },
                },
                Targets: [
                    {
                        Id: 'qrvey',
                        Arn: { Ref: 'QrveyEventBusArn' },
                        RoleArn: { 'Fn::GetAtt': ['RunCompletionRole', 'Arn'] },
                    },
                ],
            },
        },
        /**
         * Lets EventBridge deliver that one rule to that one bus.
         *
         * Assumable only by EventBridge, and only from this account — without the
         * source-account condition any EventBridge rule anywhere could use it.
         */
        RunCompletionRole: {
            Type: 'AWS::IAM::Role',
            Condition: 'ReportsCompletion',
            Properties: {
                AssumeRolePolicyDocument: {
                    Version: '2012-10-17',
                    Statement: [
                        {
                            Effect: 'Allow',
                            Principal: { Service: 'events.amazonaws.com' },
                            Action: 'sts:AssumeRole',
                            Condition: { StringEquals: { 'aws:SourceAccount': { Ref: 'AWS::AccountId' } } },
                        },
                    ],
                },
                Policies: [
                    {
                        PolicyName: 'ReportRunCompletion',
                        PolicyDocument: {
                            Version: '2012-10-17',
                            Statement: [
                                {
                                    Effect: 'Allow',
                                    Action: 'events:PutEvents',
                                    // Qrvey's bus and no other. A wildcard here would let this
                                    // role publish into any bus this account can reach.
                                    Resource: { Ref: 'QrveyEventBusArn' },
                                },
                            ],
                        },
                    },
                ],
            },
        },
        /**
         * Announces that the grant lapsed. Notification, not enforcement.
         *
         * Enforcement is the IAM condition above, which cannot fail to fire. This
         * exists so a lapsed grant is *visible* — the failure mode of a purely
         * declarative control is that nobody notices it took effect.
         */
    },
    Outputs: {
        DispatcherArn: {
            Description: 'Give this to Qrvey. It is the only thing they can invoke here.',
            Value: { 'Fn::GetAtt': ['Dispatcher', 'Arn'] },
        },
        DispatcherVersion: { Value: DISPATCHER_VERSION },
        /**
         * Which redaction rules this stack's runner masks logs with (IM-84).
         *
         * Its own number rather than implied by `DispatcherVersion`: the rules have
         * changed without the template version moving before (IM-81), and the
         * question a security review asks is about the rules, not the stack.
         */
        RedactionRulesVersion: {
            Description: 'Which redaction rules mask run logs here. docs/log-redaction.md lists them.',
            Value: REDACTION_RULES_VERSION,
        },
        ReportsCompletion: {
            Description: 'Whether this stack tells Qrvey when a run finishes.',
            Value: { 'Fn::If': ['ReportsCompletion', 'yes', 'no'] },
        },
        /**
         * Which secret ECS reads the registry credentials from.
         *
         * The ARN, not the value — outputs are readable by anyone holding
         * `cloudformation:DescribeStacks`, which is exactly why the external ID has
         * no output. This is where to put a rotated token, and it answers "did this
         * stack make one, or is it using mine".
         */
        RegistryCredentialsSecret: {
            Description: 'The secret ECS reads registry credentials from. Rotate the value here.',
            Value: {
                'Fn::If': [
                    'HasRegistryCredentials',
                    {
                        'Fn::If': [
                            'CreatesRegistrySecret',
                            { Ref: 'RegistrySecret' },
                            { Ref: 'RegistryCredentialsSecretArn' },
                        ],
                    },
                    'none - the runner image must be in a public registry',
                ],
            },
        },
        // No external ID output: DescribeStacks would defeat its NoEcho. The
        // registry credentials are the same: this names the secret, never its value.
    },
});
//# sourceMappingURL=dispatcher-template.js.map